import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Breadcrumbs from "@/components/Breadcrumbs";
import useSeo from "@/lib/useSeo";
import { BLOG_POSTS } from "@/lib/blogPosts";

export default function BlogIndex() {
  useSeo({
    title: "AeroAcres Blog — Drone Spraying Insights for Florida Growers",
    description:
      "Practical articles on drone crop spraying, FAA Part 137 certification, cost economics, and best practices for Florida avocado, tomato, citrus and nursery growers.",
    path: "/blog/",
  });

  return (
    <div className="min-h-screen bg-background" data-testid="blog-index">
      <Header />
      <section className="pt-[110px] pb-10 bg-muted/40 border-b border-border">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <Breadcrumbs items={[{ label: "Blog" }]} />
          <h1 className="font-display mt-4 text-4xl sm:text-5xl text-primary font-bold tracking-tight">
            Drone spraying insights for Florida growers
          </h1>
          <p className="mt-4 text-lg text-muted-foreground max-w-2xl">
            Field-tested writing from working FAA Part 137 pilots — regulation, agronomy, economics and the real-world details every Florida grower wants to know.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-background">
        <div className="max-w-6xl mx-auto px-5 md:px-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {BLOG_POSTS.map((p) => (
            <Link
              key={p.slug}
              to={`/blog/${p.slug}/`}
              className="group flex flex-col bg-card border border-border rounded-3xl overflow-hidden shadow-card hover:shadow-card-hover hover:border-accent/40 transition-all"
              data-testid={`blog-card-${p.slug}`}
            >
              <div className="relative h-48 overflow-hidden">
                <img src={p.cover} alt={p.coverAlt} loading="lazy" className="h-full w-full object-cover transition-transform duration-[700ms] group-hover:scale-105" />
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {new Date(p.date).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}</span>
                  <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {p.readMinutes} min read</span>
                </div>
                <h2 className="mt-3 font-display text-xl font-bold text-primary group-hover:text-accent transition-colors leading-tight">{p.title}</h2>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed flex-1">{p.description}</p>
                <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary group-hover:text-accent transition-colors">
                  Read article <ArrowRight className="h-3.5 w-3.5" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
